import { calculateAll } from './taxCalculations';

/**
 * Calculates the difference between two values (newValue - oldValue)
 * @param {number} oldValue - The original value
 * @param {number} newValue - The new value to compare
 * @returns {number} The difference
 */
export const calculateDifference = (oldValue, newValue) => {
  return newValue - oldValue;
};

/**
 * Calculates the percentage change between two values
 * @param {number} oldValue - The original value
 * @param {number} newValue - The new value to compare
 * @returns {number} The percentage change
 */
export const calculatePercentageChange = (oldValue, newValue) => {
  if (oldValue === 0) {
    return newValue === 0 ? 0 : 100; // Convention: treat as 100% when base is 0
  }
  return ((newValue - oldValue) / oldValue) * 100;
};

/**
 * Calculates comprehensive comparison between two salary scenarios
 * @param {Object} userInputA - First salary configuration
 * @param {Object} userInputB - Second salary configuration  
 * @param {Object} config - Tax configuration object
 * @returns {Object} Comprehensive comparison results
 */
export const calculateComparison = (userInputA, userInputB, config) => {
  // Calculate results for both salaries
  const salaryA = calculateAll(userInputA, config);
  const salaryB = calculateAll(userInputB, config);

  // Calculate total compensation value - different logic for different pension types
  const calculateTotalCompensation = (salaryResult, userInput) => {
    if (!userInput.pension?.active) {
      return salaryResult.takeHome;
    }
    
    if (userInput.pension.scheme === 'Personal') {
      // For Personal pensions, add take-home plus the value of tax relief
      const personalPensionBenefit = salaryResult.personalPension?.estimatedRebate || 0;
      return salaryResult.takeHome + personalPensionBenefit;
    } else {
      // For Salary Sacrifice/Auto-enrolment/Employer pensions, add the pension value
      // The take-home already reflects the tax/NI savings from reduced gross salary
      return salaryResult.takeHome + (salaryResult.pensionDeduction || 0);
    }
  };

  const totalCompensationA = calculateTotalCompensation(salaryA, userInputA);
  const totalCompensationB = calculateTotalCompensation(salaryB, userInputB);

  // Calculate differences for all key metrics
  const differences = {
    totalGrossIncome: calculateDifference(salaryA.totalGrossIncome, salaryB.totalGrossIncome),
    takeHome: calculateDifference(salaryA.takeHome, salaryB.takeHome),
    taxPaid: calculateDifference(salaryA.taxPaid, salaryB.taxPaid),
    nationalInsurance: calculateDifference(salaryA.nationalInsurance, salaryB.nationalInsurance),
    pensionDeduction: calculateDifference(salaryA.pensionDeduction, salaryB.pensionDeduction),
    studentLoanRepayment: calculateDifference(salaryA.studentLoanRepayment || 0, salaryB.studentLoanRepayment || 0),
    marginalRate: calculateDifference(salaryA.marginalRate, salaryB.marginalRate),
    totalCompensation: calculateDifference(totalCompensationA, totalCompensationB)
  };

  // Calculate percentage changes for all key metrics
  const percentageChanges = {
    totalGrossIncome: calculatePercentageChange(salaryA.totalGrossIncome, salaryB.totalGrossIncome),
    takeHome: calculatePercentageChange(salaryA.takeHome, salaryB.takeHome),
    taxPaid: calculatePercentageChange(salaryA.taxPaid, salaryB.taxPaid),
    nationalInsurance: calculatePercentageChange(salaryA.nationalInsurance, salaryB.nationalInsurance),
    pensionDeduction: calculatePercentageChange(salaryA.pensionDeduction, salaryB.pensionDeduction),
    studentLoanRepayment: calculatePercentageChange(salaryA.studentLoanRepayment || 0, salaryB.studentLoanRepayment || 0),
    marginalRate: calculatePercentageChange(salaryA.marginalRate, salaryB.marginalRate),
    totalCompensation: calculatePercentageChange(totalCompensationA, totalCompensationB)
  };

  // Enhanced decision logic considering both take-home and total compensation
  let betterOption;
  let betterOptionReason;
  
  if (differences.totalCompensation > 0) {
    betterOption = 'B';
    if (differences.takeHome > 0) {
      betterOptionReason = 'Higher take-home pay and total compensation';
    } else {
      betterOptionReason = 'Higher total compensation (including pension contributions)';
    }
  } else if (differences.totalCompensation < 0) {
    betterOption = 'A';
    if (differences.takeHome < 0) {
      betterOptionReason = 'Higher take-home pay and total compensation';
    } else {
      betterOptionReason = 'Higher total compensation (including pension contributions)';
    }
  } else {
    betterOption = 'equal';
    betterOptionReason = 'Equal total compensation value';
  }

  return {
    salaryA: {
      ...salaryA,
      totalCompensation: totalCompensationA
    },
    salaryB: {
      ...salaryB,
      totalCompensation: totalCompensationB
    },
    differences,
    percentageChanges,
    betterOption,
    betterOptionReason
  };
};